<template>
  <form @submit.prevent="onSubmit" class="space-y-4">
    <TextInput
        :label="$t('message.username.label')"
        type="text"
        :placeholder="$t('message.username.placeholder')"
        name="username"
        v-model="username"
        :error="usernameError"
        classInput="h-[48px]"
    />
    <TextInput
        :label="$t('message.password.label')"
        type="password"
        :placeholder="$t('message.password.placeholder')"
        name="password"
        v-model="password"
        :error="passwordError"
        hasicon
        classInput="h-[48px]"
    />

    <div class="flex justify-between">
      <label class="cursor-pointer flex items-start">
        <input
            type="checkbox"
            class="hidden"
            @change="() => (checkbox = !checkbox)"
        />
        <span
            class="h-4 w-4 border rounded flex-none inline-flex ltr:mr-3 rtl:ml-3 relative top-1 transition-all duration-150"
            :class="
            checkbox
              ? 'ring-2 ring-black-500 dark:ring-offset-slate-600 dark:ring-slate-900  dark:bg-slate-900 ring-offset-2 bg-slate-900'
              : 'bg-slate-100 dark:bg-slate-600 border-slate-100 dark:border-slate-600 '
          "
        >
          <img
              src="@/assets/images/icon/ck-white.svg"
              alt=""
              class="h-[10px] w-[10px] block m-auto"
              v-if="checkbox"
          />
        </span>
        <span class="text-slate-500 dark:text-slate-400 text-sm leading-6"
        >{{ $t('message.keep_me_signed_in') }}</span
        >
      </label>
      <router-link
          to="/forgot-password"
          class="text-sm text-slate-800 dark:text-slate-400 leading-6 font-medium"
      >{{ $t('message.forgot_password') }}
      </router-link
      >
    </div>

    <button type="submit" class="btn btn-dark block w-full text-center flex items-center justify-center gap-3">
      {{ $t('message.signin') }}
      <Icon v-if="loading" icon="eos-icons:bubble-loading" height="24"/>
    </button>
  </form>
</template>
<script>
import TextInput from "@/components/Textinput/index.vue";
import {useField, useForm} from "vee-validate";
import * as yup from "yup";
import {useRouter} from "vue-router";
import {useToast} from "vue-toastification";
import {reactive, ref, computed} from "vue";
import {Icon} from "@iconify/vue";
import {useUserStore} from "@/stores/user.js";
import {useThemeSettingsStore} from "@/stores/themeSettings.js";
import {useI18n} from "vue-i18n";
import {useAbility} from '@casl/vue';
import {createAbility} from '@/services/ability';

export default {
  components: {
    TextInput,
    Icon,
  },
  data() {
    return {
      checkbox: false,
    };
  },
  setup() {
    const {t} = useI18n({useScope: 'global'})
    const userStore = useUserStore();
    const themeSettings = useThemeSettingsStore();
    const loading = ref(false);
    const ability = useAbility();

    const user = reactive({
      username: null,
      password: null,
    });

    // Define a validation schema
    const schema = yup.object({
      username: yup.string().required("Username is required").min(3),
      password: yup.string().required("Password is required").min(1),
    });

    const toast = useToast();
    const router = useRouter();

    const formValues = {
      username: "",
      password: "",
    };

    const {handleSubmit} = useForm({
      validationSchema: schema,
      initialValues: formValues,
    });
    // No need to define rules for fields

    const {value: username, errorMessage: usernameError} = useField("username");
    const {value: password, errorMessage: passwordError} =
        useField("password");
    const onSubmit = handleSubmit(async (values) => {
      loading.value = true;
      userStore.setErrorMessage('')
      const previousUsername = userStore.getUsername
      await userStore.action_login({
        username: values.username,
        password: values.password,
      })
      const loggedIn = userStore.getCredential;
      const newAbilities = createAbility(userStore.getPermissions);
      ability.update(newAbilities); // Update abilities after login
      if (loggedIn) {
        const currentUsername = userStore.getUsername
        if (previousUsername !== currentUsername) {
          userStore.setBoostGamePreferences([])
          userStore.setAccountGamePreferences([])
          userStore.setCurrencyBuyGamePreferences([])
          userStore.setCurrencySellGamePreferences([])
        }
        await router.push({name: "Dashboard"});
        const loginMessage = t('message.login successfully')
        toast.success(loginMessage, {
          timeout: 2000,
          rtl: themeSettings.direction,
          toastClassName: "!mr-0 !ml-auto",
        });
      } else {
        toast.error(message.value, {
          timeout: 2000,
        });
      }
      loading.value = false;
    });

    const message = computed(() => {
      return userStore.getErrorMessage;
    });

    return {
      username,
      user,
      usernameError,
      password,
      passwordError,
      onSubmit,
      message,
      loading
    };
  },
};
</script>
<style lang="scss">
.mr-0 {
  margin-right: 0 !important;
}

.ml-auto {
  margin-left: auto !important;
}
</style>
